import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

/**
 * Custom hook that wraps useNavigate to support command+click for opening links in new windows
 * Returns a function that can be used like navigate, but also accepts an event parameter
 * to check for modifier keys
 */
export const useNavigateWithCommandClick = () => {
  const navigate = useNavigate();

  const navigateWithCommandClick = useCallback(
    (to, options = {}, event = null) => {
      // If event is provided and command/ctrl key is pressed, open in new window
      if (event && (event.metaKey || event.ctrlKey)) {
        // Prevent default navigation behavior
        event.preventDefault();

        // Convert relative paths to absolute URLs for new window
        const url = to.startsWith("/") ? to : `/${to}`;
        window.open(url, "_blank");
      } else {
        // Regular navigation
        navigate(to, options);
      }
    },
    [navigate]
  );

  return navigateWithCommandClick;
};

/**
 * Helper function to create click handlers that support command+click
 * Usage: onClick={createNavigationHandler('/path', navigate)}
 */
export const createNavigationHandler = (to, navigate, options = {}) => {
  // Return a memoized handler to prevent unnecessary re-renders
  return (event) => {
    navigate(to, options, event);
  };
};
